import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  FaStar, FaUsers, FaRulerCombined, FaGasPump, FaCog, 
  FaCheckCircle, FaWhatsapp, FaPhone, FaArrowLeft, FaMapMarkerAlt,
  FaSnowflake, FaTv, FaWifi, FaSolarPanel, FaShower, FaUtensils
} from 'react-icons/fa';
import fleetData from '../data/fleet.json';
import Navbar from './Navbar';
import Footer from './Footer';

const FleetDetail = () => {
  const { id } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [activeTab, setActiveTab] = useState('features');

  useEffect(() => {
    const vehicleData = fleetData.find(v => v.id === id);
    if (vehicleData) {
      setVehicle(vehicleData);
      setSelectedImage(0);
    }
  }, [id]);

  if (!vehicle) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-primary-800 mb-4">Vehicle not found</h2>
          <Link to="/" className="text-accent-600 hover:text-accent-700 font-semibold">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  const handleBookNow = () => {
    const message = `Hello! I'm interested in booking the ${vehicle.name}.%0A%0ACapacity: ${vehicle.capacity}%0A%0APlease provide more details.`;
    window.open(`https://wa.me/918380866111?text=${message}`, '_blank');
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 pt-20">
      {/* Header */}
      <div className="bg-white shadow-md py-6 sticky top-0 z-40">
        <div className="container-custom flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2 text-primary-700 hover:text-accent-600 transition-colors">
            <FaArrowLeft />
            <span className="font-semibold">Back to Home</span>
          </Link>
          <div className="flex items-center space-x-4">
            <a href="tel:+918380866111" className="hidden sm:flex items-center space-x-2 text-primary-700 hover:text-accent-600 transition-colors">
              <FaPhone />
              <span className="font-semibold">+91 83808-66111</span>
            </a>
            <button
              onClick={handleBookNow}
              className="bg-green-500 text-white px-6 py-2 rounded-full hover:bg-green-600 transition-colors flex items-center space-x-2"
            >
              <FaWhatsapp />
              <span className="font-semibold">Book Now</span>
            </button>
          </div>
        </div>
      </div>

      <div className="container-custom py-12">
        {/* Title Section */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 text-sm text-primary-600 mb-2">
            <Link to="/" className="hover:text-accent-600">Home</Link>
            <span>/</span>
            <Link to="/#fleet" className="hover:text-accent-600">Fleet</Link>
            <span>/</span>
            <span className="text-accent-600">{vehicle.name}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-primary-800 mb-4">{vehicle.name}</h1>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-1">
              {[...Array(5)].map((_, i) => (
                <FaStar key={i} className={`${i < Math.floor(vehicle.rating) ? 'text-accent-500' : 'text-gray-300'}`} />
              ))}
              <span className="ml-2 text-primary-700 font-semibold">{vehicle.rating}</span>
              <span className="text-primary-600">({vehicle.reviews} reviews)</span>
            </div>
            <span className="px-4 py-1 bg-accent-100 text-accent-700 rounded-full font-semibold">{vehicle.category}</span>
          </div>
        </div>

        {/* Image Gallery */}
        <div className="mb-12">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl mb-4">
            <img
              src={vehicle.images[selectedImage]}
              alt={vehicle.name}
              className="w-full h-[500px] object-cover"
            />
            <div className="absolute bottom-4 right-4 bg-black/50 text-white px-4 py-2 rounded-full text-sm font-semibold backdrop-blur-sm">
              {selectedImage + 1} / {vehicle.images.length}
            </div>
          </div>
          <div className="grid grid-cols-4 gap-4">
            {vehicle.images.map((img, index) => (
              <button
                key={index}
                onClick={() => setSelectedImage(index)}
                className={`rounded-xl overflow-hidden ${selectedImage === index ? 'ring-4 ring-accent-500' : 'opacity-70 hover:opacity-100'} transition-all`}
              >
                <img src={img} alt={`${vehicle.name} ${index + 1}`} className="w-full h-24 object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Video Section */}
        {vehicle.videoUrl && (
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-primary-800 mb-6">Watch Virtual Tour</h2>
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <iframe
                width="100%"
                height="500"
                src={vehicle.videoUrl}
                title={`${vehicle.name} Virtual Tour`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full"
              ></iframe>
            </div>
          </div>
        )}

        {/* Quick Info Cards */}
        <div className="grid md:grid-cols-4 gap-4 mb-12">
          <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
            <FaUsers className="text-4xl text-accent-500 mx-auto mb-3" />
            <p className="text-primary-600 text-sm mb-1">Capacity</p>
            <p className="text-xl font-bold text-primary-800">{vehicle.capacity}</p>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
            <FaRulerCombined className="text-4xl text-primary-500 mx-auto mb-3" />
            <p className="text-primary-600 text-sm mb-1">Length</p>
            <p className="text-xl font-bold text-primary-800">{vehicle.specifications.length}</p>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
            <FaGasPump className="text-4xl text-forest-500 mx-auto mb-3" />
            <p className="text-primary-600 text-sm mb-1">Fuel Type</p>
            <p className="text-xl font-bold text-primary-800">{vehicle.specifications.fuelType}</p>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
            <FaCog className="text-4xl text-accent-600 mx-auto mb-3" />
            <p className="text-primary-600 text-sm mb-1">Transmission</p>
            <p className="text-xl font-bold text-primary-800">{vehicle.specifications.transmission}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="flex space-x-4 border-b-2 border-primary-200">
            {['features', 'specifications', 'amenities', 'included'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-3 font-semibold capitalize transition-colors ${
                  activeTab === tab
                    ? 'text-accent-600 border-b-4 border-accent-600 -mb-0.5'
                    : 'text-primary-600 hover:text-accent-600'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-3xl p-8 shadow-lg mb-12">
          {activeTab === 'features' && (
            <div>
              <h2 className="text-3xl font-bold text-primary-800 mb-6">Features & Highlights</h2>
              <p className="text-lg text-primary-600 mb-6">{vehicle.description}</p>
              <div className="grid md:grid-cols-2 gap-4">
                {vehicle.features.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3 p-4 bg-primary-50 rounded-xl">
                    <FaCheckCircle className="text-accent-500 mt-1 flex-shrink-0" />
                    <span className="text-primary-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'specifications' && (
            <div>
              <h2 className="text-3xl font-bold text-primary-800 mb-6">Technical Specifications</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {Object.entries(vehicle.specifications).map(([key, value]) => (
                  <div key={key} className="flex justify-between items-center p-4 bg-primary-50 rounded-xl">
                    <span className="font-semibold text-primary-700 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                    <span className="text-primary-800 font-bold">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'amenities' && (
            <div>
              <h2 className="text-3xl font-bold text-primary-800 mb-6">Amenities & Facilities</h2>
              <div className="grid md:grid-cols-3 gap-4">
                {vehicle.amenities.map((amenity, index) => (
                  <div key={index} className="flex items-center space-x-3 p-4 bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl">
                    <div className="w-10 h-10 bg-accent-500 rounded-full flex items-center justify-center text-white">
                      {getAmenityIcon(amenity)}
                    </div>
                    <span className="text-primary-700 font-medium">{amenity}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'included' && (
            <div>
              <h2 className="text-3xl font-bold text-primary-800 mb-6">What's Included</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {vehicle.included.map((item, index) => (
                  <div key={index} className="flex items-start space-x-3 p-4 bg-green-50 rounded-xl border-2 border-green-200">
                    <FaCheckCircle className="text-green-500 mt-1 flex-shrink-0 text-xl" />
                    <span className="text-primary-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-primary-600 to-forest-600 rounded-3xl p-12 text-center text-white shadow-2xl">
          <h2 className="text-4xl font-bold mb-4">Ready to Hit the Road?</h2>
          <p className="text-xl mb-8 text-primary-100">Book this {vehicle.name} now and start your adventure!</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleBookNow}
              className="bg-green-500 text-white px-8 py-4 rounded-full hover:bg-green-600 transition-colors flex items-center justify-center space-x-2 text-lg font-bold shadow-xl"
            >
              <FaWhatsapp className="text-2xl" />
              <span>Book via WhatsApp</span>
            </button>
            <a
              href="tel:+918380866111"
              className="bg-white text-primary-700 px-8 py-4 rounded-full hover:bg-primary-50 transition-colors flex items-center justify-center space-x-2 text-lg font-bold shadow-xl"
            >
              <FaPhone />
              <span>Call Now</span>
            </a>
          </div>
        </div>
      </div>

      </div>
      <Footer />
    </>
  );
};

const getAmenityIcon = (amenity) => {
  const lowerAmenity = amenity.toLowerCase();
  if (lowerAmenity.includes('wifi') || lowerAmenity.includes('connectivity')) return <FaWifi />;
  if (lowerAmenity.includes('tv')) return <FaTv />;
  if (lowerAmenity.includes('shower') || lowerAmenity.includes('bathroom')) return <FaShower />;
  if (lowerAmenity.includes('solar')) return <FaSolarPanel />;
  if (lowerAmenity.includes('kitchen') || lowerAmenity.includes('gas')) return <FaUtensils />;
  if (lowerAmenity.includes('ac') || lowerAmenity.includes('air')) return <FaSnowflake />;
  if (lowerAmenity.includes('gps') || lowerAmenity.includes('navigation')) return <FaMapMarkerAlt />;
  return <FaCheckCircle />;
};

export default FleetDetail;
